import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, Quote } from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';
import { getTestimonials, getReviews } from '../api/api';

export const Testimonials = () => {
  // Deux blocs d'avis à la suite noient le message. Quand les avis Google sont
  // actifs, ils font foi : les témoignages saisis à la main passent en retrait.
  const [avisGoogleActifs, setAvisGoogleActifs] = useState(false);

  useEffect(() => {
    getReviews()
      .then((d) => setAvisGoogleActifs(Boolean(d?.enabled && d?.rating)))
      .catch(() => setAvisGoogleActifs(false));
  }, []);

  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await getTestimonials();
        setTestimonials(data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  // Les avis Google, vérifiables, priment sur les témoignages saisis.
  if (avisGoogleActifs) return null;

  if (loading) {
    return (
      <section id="testimonials" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">Chargement des avis...</div>
          </div>
        </div>
      </section>
    );
  }

  // Rien de saisi : on masque toute la section plutôt que d'afficher un titre
  // suivi d'un vide (cohérent avec la galerie).
  if (testimonials.length === 0) return null;

  return (
    <section id="testimonials" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 mb-4">
            Avis de nos clients
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Découvrez les témoignages de nos clients satisfaits
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <Card
              key={testimonial.id}
              className="relative border-0 bg-white hover:shadow-lg transition-all duration-500 hover:-translate-y-0.5 overflow-hidden group"
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              {/* Quote Icon Background */}
              <div className="absolute top-0 right-0 text-blue-50 opacity-50 group-hover:opacity-100 transition-opacity duration-500">
                <Quote className="w-24 h-24 -mr-4 -mt-4" fill="currentColor" />
              </div>

              <CardContent className="p-8 relative z-10">
                {/* Stars */}
                <div className="flex space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>

                {/* Author Info */}
                <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
                  <Avatar className="w-12 h-12 bg-blue-600">
                    <AvatarFallback className="text-white font-semibold">
                      {testimonial.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.company}</div>
                  </div>
                </div>
              </CardContent>

              {/* Decorative Border */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-green-400 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
            </Card>
          ))}
        </div>

        {/* Additional Testimonials Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-5xl mx-auto">
          {testimonials.slice(3).map((testimonial, index) => (
            <Card
              key={testimonial.id}
              className="relative border-0 bg-white hover:shadow-lg transition-all duration-500 hover:-translate-y-0.5 overflow-hidden group"
              style={{
                animationDelay: `${(index + 3) * 100}ms`
              }}
            >
              {/* Quote Icon Background */}
              <div className="absolute top-0 right-0 text-blue-50 opacity-50 group-hover:opacity-100 transition-opacity duration-500">
                <Quote className="w-24 h-24 -mr-4 -mt-4" fill="currentColor" />
              </div>

              <CardContent className="p-8 relative z-10">
                {/* Stars */}
                <div className="flex space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>

                {/* Author Info */}
                <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
                  <Avatar className="w-12 h-12 bg-blue-600">
                    <AvatarFallback className="text-white font-semibold">
                      {testimonial.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.company}</div>
                  </div>
                </div>
              </CardContent>

              {/* Decorative Border */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-green-400 to-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
